import { useEffect, useState, useCallback } from "react";
import api, { errMsg } from "../lib/api";
import { fmtIDR } from "../lib/format";
import { PageHeader, Card, Badge, Field, Btn, inputCls } from "../components/common";
import { useAuth } from "../context/AuthContext";
import { Calculator, MapPin, UserPlus } from "lucide-react";
import { toast } from "sonner";

export default function SalesMobile() {
  const { user } = useAuth();
  const [stock, setStock] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", source: "Walk-in", value: 0, notes: "" });
  const [calc, setCalc] = useState({ price: 450000000, dp_pct: 20, rate: 6.5, years: 15, method: "kpr" });

  const load = useCallback(() => {
    api.get("/sales/stock-summary").then((r) => setStock(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const submitLead = async (e) => {
    e.preventDefault();
    try {
      await api.post("/crm/leads", { ...form, assigned_to: user.name });
      toast.success("Prospek tersimpan ke CRM");
      setForm({ name: "", phone: "", source: "Walk-in", value: 0, notes: "" });
    } catch (e) { toast.error(errMsg(e)); }
  };

  const dp = Math.round((calc.price * calc.dp_pct) / 100);
  const principal = calc.price - dp;
  const r = calc.rate / 100 / 12;
  const n = calc.years * 12;
  const kprMonthly = r > 0 ? Math.round((principal * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1)) : 0;
  const installmentMonthly = Math.round(principal / 12);

  return (
    <div className="max-w-2xl mx-auto" data-testid="sales-mobile-page">
      <PageHeader title="Sales Lapangan" subtitle="Input prospek harian & cek stok unit real-time" testid="sales-mobile-header" />

      {stock && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-6" data-testid="stock-summary">
          {[["tersedia", "emerald"], ["booking", "amber"], ["terjual", "rose"], ["hold", "indigo"]].map(([k, tone]) => (
            <Card key={k} className="!p-3 text-center" testid={`stock-${k}`}>
              <div className="text-2xl font-semibold font-mono text-slate-100">{stock.summary[k] || 0}</div>
              <Badge tone={tone}>{k}</Badge>
            </Card>
          ))}
        </div>
      )}

      <Card className="mb-6" testid="quick-lead-card">
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <UserPlus className="w-4 h-4 text-emerald-400" />
          <h3 className="text-sm font-medium text-slate-200">Input Prospek Harian</h3>
        </div>
        <form onSubmit={submitLead} className="space-y-3" data-testid="quick-lead-form">
          <Field label="Nama Prospek"><input required className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="quick-lead-name" /></Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Telepon"><input className={inputCls} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="quick-lead-phone" /></Field>
            <Field label="Sumber">
              <select className={inputCls} value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} data-testid="quick-lead-source">
                {["Walk-in", "Instagram", "Facebook", "Referral", "Pameran", "Canvassing"].map((s) => <option key={s}>{s}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Catatan"><textarea className={inputCls} rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="quick-lead-notes" /></Field>
          <Btn type="submit" className="w-full justify-center" data-testid="quick-lead-submit">Simpan ke CRM</Btn>
        </form>
      </Card>

      <Card className="mb-6" testid="available-units-card">
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <MapPin className="w-4 h-4 text-emerald-400" />
          <h3 className="text-sm font-medium text-slate-200">Unit Tersedia Saat Ini</h3>
        </div>
        <div className="space-y-2 max-h-72 overflow-y-auto">
          {(stock?.available || []).map((u) => (
            <div key={u.id} className="flex items-center justify-between bg-slate-950 border border-slate-800 rounded-lg px-3 py-2.5" data-testid={`available-unit-${u.block}${u.number}`}>
              <div>
                <span className="font-mono text-sm text-slate-100">{u.block}-{u.number}</span>
                <span className="text-xs text-slate-500 ml-2">Tipe {u.type}</span>
              </div>
              <span className="font-mono text-xs text-emerald-400">{fmtIDR(u.price)}</span>
            </div>
          ))}
          {stock?.available?.length === 0 && <p className="text-slate-500 text-sm">Semua unit habis.</p>}
        </div>
      </Card>

      <Card testid="installment-calc-card">
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <Calculator className="w-4 h-4 text-emerald-400" />
          <h3 className="text-sm font-medium text-slate-200">Simulasi Pembayaran</h3>
        </div>
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Harga Unit"><input type="number" className={inputCls} value={calc.price} onChange={(e) => setCalc({ ...calc, price: +e.target.value })} data-testid="calc-price" /></Field>
            <Field label="DP (%)"><input type="number" className={inputCls} value={calc.dp_pct} onChange={(e) => setCalc({ ...calc, dp_pct: +e.target.value })} data-testid="calc-dp" /></Field>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Suku Bunga KPR (%/thn)"><input type="number" step="0.1" className={inputCls} value={calc.rate} onChange={(e) => setCalc({ ...calc, rate: +e.target.value })} data-testid="calc-rate" /></Field>
            <Field label="Tenor (tahun)"><input type="number" className={inputCls} value={calc.years} onChange={(e) => setCalc({ ...calc, years: +e.target.value })} data-testid="calc-years" /></Field>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
            <div className="bg-slate-950 border border-emerald-500/30 rounded-lg p-3" data-testid="calc-kpr-result">
              <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">KPR / bulan</div>
              <div className="font-mono text-emerald-400 text-lg">{fmtIDR(kprMonthly)}</div>
              <div className="text-[10px] text-slate-500 mt-1">DP: {fmtIDR(dp)}</div>
            </div>
            <div className="bg-slate-950 border border-cyan-500/30 rounded-lg p-3" data-testid="calc-cash-result">
              <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Cash Bertahap 12x</div>
              <div className="font-mono text-cyan-400 text-lg">{fmtIDR(installmentMonthly)}</div>
              <div className="text-[10px] text-slate-500 mt-1">DP: {fmtIDR(dp)}</div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
